import React, { useState, useEffect, useMemo } from 'react';
import {
  ClipboardList, AlertTriangle, Loader2, Check, X, ChevronDown, ChevronUp, FileText, Search, SlidersHorizontal,
  Clock, CheckCircle2, Wallet,
} from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { getPurchaseRequests } from 'core/api/apiService';
import MultiSelectDropdown from './MultiSelectDropdown';
import AutoFilterToggle from './AutoFilterToggle';

/**
 * BanfStatusView — Sidebar-Sektion "BANF-Status". Zeigt die BANFs des Users
 * (rollen-gescoped, GET /purchase-requests) mit echten EBAN/EBKN-Feldern:
 * Mehrfachauswahl-Filter (Status, Werk, Warengruppe, Anforderer, Kostenstelle,
 * Auftrag) im Lagerbestand-Stil, Sortierung (auch nach Kostenstelle/Auftrag),
 * Detail-Expand mit Kontierung je Typ, Status-Stepper, echtes Lieferrisiko
 * (Bedarfstermin nah/überfällig UND noch nicht bestellt), KPI-Drilldown.
 */
const STEPS = ['banfStepErstellt', 'banfStepGenehmigt', 'banfStepBestellt', 'banfStepGeliefert'];
const RISK_DAYS = 7;

function stepState(status, idx) {
  if (status === 'rejected') return idx === 0 ? 'done' : idx === 1 ? 'rejected' : 'pending';
  if (status === 'approved') return idx <= 1 ? 'done' : 'pending';
  return idx === 0 ? 'done' : idx === 1 ? 'current' : 'pending';
}

const BanfStatusView = ({ user }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [requests, setRequests] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState([]);
  const [werkF, setWerkF] = useState([]);
  const [wgF, setWgF] = useState([]);
  const [anfF, setAnfF] = useState([]);
  const [ksF, setKsF] = useState([]);
  const [auftragF, setAuftragF] = useState([]);
  const [skF, setSkF] = useState([]);
  const [egF, setEgF] = useState([]);
  const [bedarfVon, setBedarfVon] = useState('');
  const [bedarfBis, setBedarfBis] = useState('');
  const [riskOnly, setRiskOnly] = useState(false);
  const [sortBy, setSortBy] = useState('bedarf');
  const [sortDir, setSortDir] = useState('asc');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getPurchaseRequests(user)
      .then((res) => { if (!cancelled) setRequests(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) setError(err?.message || String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const enrich = (r) => {
    const items = Array.isArray(r.items) ? r.items : [];
    const dates = items.map((i) => i.delivery_date).filter(Boolean).map((s) => new Date(s)).filter((d) => !Number.isNaN(d.getTime()));
    const bedarf = dates.length ? new Date(Math.min(...dates.map((d) => d.getTime()))) : null;
    const uniq = (sel) => [...new Set(items.map(sel).filter(Boolean))];
    const overdueRisk = bedarf ? (() => { const x = new Date(bedarf); x.setHours(0, 0, 0, 0); const limit = new Date(today); limit.setDate(limit.getDate() + RISK_DAYS); return x <= limit; })() : false;
    const atRisk = (r.status === 'pending' || r.status === 'approved') && overdueRisk;
    return {
      ...r,
      _items: items,
      _bedarf: bedarf,
      _werke: uniq((i) => i.plant),
      _wgs: uniq((i) => i.product_group || i.material_group),
      _ks: uniq((i) => i.kostenstelle || i.cost_center),
      _auftraege: uniq((i) => i.auftrag),
      _sk: uniq((i) => i.sachkonto || i.gl_account),
      _eg: uniq((i) => i.einkauefergruppe),
      _atRisk: atRisk,
    };
  };

  const list = useMemo(() => (Array.isArray(requests) ? requests.map(enrich) : []), [requests, today]);

  const distinct = (sel) => [...new Set(list.flatMap(sel).filter(Boolean))].sort();
  const werkOpts = useMemo(() => distinct((r) => r._werke), [list]);
  const wgOpts = useMemo(() => distinct((r) => r._wgs), [list]);
  const anfOpts = useMemo(() => distinct((r) => [r.requester_name]), [list]);
  const ksOpts = useMemo(() => distinct((r) => r._ks), [list]);
  const auftragOpts = useMemo(() => distinct((r) => r._auftraege), [list]);
  const skOpts = useMemo(() => distinct((r) => r._sk), [list]);
  const egOpts = useMemo(() => distinct((r) => r._eg), [list]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const anyIn = (sel, arr) => sel.length === 0 || sel.some((v) => arr.includes(v));
    let out = list.filter((r) => {
      if (statusF.length && !statusF.includes(r.status)) return false;
      if (!anyIn(werkF, r._werke)) return false;
      if (!anyIn(wgF, r._wgs)) return false;
      if (anfF.length && !anfF.includes(r.requester_name)) return false;
      if (!anyIn(ksF, r._ks)) return false;
      if (!anyIn(auftragF, r._auftraege)) return false;
      if (!anyIn(skF, r._sk)) return false;
      if (!anyIn(egF, r._eg)) return false;
      if (bedarfVon && (!r._bedarf || r._bedarf < new Date(bedarfVon))) return false;
      if (bedarfBis && (!r._bedarf || r._bedarf > new Date(bedarfBis))) return false;
      if (riskOnly && !r._atRisk) return false;
      if (q) {
        const hay = [`banf-${String(r.id).padStart(7, '0')}`, r.requester_name, ...r._items.map((i) => `${i.description || ''} ${i.material || ''}`)].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    out = out.slice().sort((a, b) => {
      if (sortBy === 'wert') return ((a.total_amount || 0) - (b.total_amount || 0)) * dir;
      if (sortBy === 'datum') return ((new Date(a.created_at).getTime() || 0) - (new Date(b.created_at).getTime() || 0)) * dir;
      if (sortBy === 'kostenstelle') return String(a._ks[0] || '').localeCompare(String(b._ks[0] || '')) * dir;
      if (sortBy === 'auftrag') return String(a._auftraege[0] || '').localeCompare(String(b._auftraege[0] || '')) * dir;
      return ((a._bedarf ? a._bedarf.getTime() : Infinity) - (b._bedarf ? b._bedarf.getTime() : Infinity)) * dir;
    });
    return out;
  }, [list, search, statusF, werkF, wgF, anfF, ksF, auftragF, skF, egF, bedarfVon, bedarfBis, riskOnly, sortBy, sortDir]);

  const kpiPending = list.filter((r) => r.status === 'pending').length;
  const kpiApproved = list.filter((r) => r.status === 'approved').length;
  const kpiRisk = list.filter((r) => r._atRisk).length;
  const kpiWert = list.reduce((a, r) => a + (Number(r.total_amount) || 0), 0);

  const fmtEur = (v, cur) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur || 'EUR' }).format(v ?? 0);
  const fmtDate = (d) => { if (!d) return '—'; const x = d instanceof Date ? d : new Date(d); return Number.isNaN(x.getTime()) ? '—' : x.toLocaleDateString('de-DE'); };
  const banfNr = (r) => `BANF-${String(r.id).padStart(7, '0')}`;
  const firstDesc = (r) => { const it = r._items; if (!it.length) return r.requester_name || '—'; return (it[0].description || it[0].material || '—') + (it.length > 1 ? ` +${it.length - 1}` : ''); };

  const resetAll = () => { setStatusF([]); setWerkF([]); setWgF([]); setAnfF([]); setKsF([]); setAuftragF([]); setSkF([]); setEgF([]); setBedarfVon(''); setBedarfBis(''); setRiskOnly(false); setSearch(''); };
  const advCount = anfF.length + ksF.length + auftragF.length + skF.length + egF.length + (bedarfVon || bedarfBis ? 1 : 0) + (riskOnly ? 1 : 0);
  const anyActive = statusF.length || werkF.length || wgF.length || advCount || search;

  // ---- Styling ----
  const card = isDarkMode ? 'bg-white/6 border border-white/12 rounded-2xl p-4' : 'bg-white border border-slate-200 rounded-2xl p-4 shadow-sm';
  const labelCls = isDarkMode ? 'text-xs text-white/50 font-medium' : 'text-xs text-slate-500 font-medium';
  const valueCls = isDarkMode ? 'text-2xl font-bold text-white' : 'text-2xl font-bold text-slate-900';
  const mutedCls = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const valCls = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const inputCls = `px-3 py-2 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-white/8 border-white/15 text-white placeholder-white/30 focus:border-brand/50' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-brand/50'}`;
  const kpiCard = (active) => `${card} cursor-pointer transition-all ${active ? (isDarkMode ? 'ring-2 ring-brand/50' : 'ring-2 ring-brand/40') : (isDarkMode ? 'hover:bg-white/10' : 'hover:shadow-md')}`;
  const riskBadge = isDarkMode ? 'text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-500/20 text-red-300' : 'text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700';
  const chipBase = 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none';
  const chipActive = isDarkMode ? 'bg-brand/25 border-brand/50 text-blue-200' : 'bg-brand/10 border-brand/40 text-brand';
  const chipInactive = isDarkMode ? 'bg-white/8 border-white/15 text-white/60 hover:bg-white/12' : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';

  const KPI_ACCENTS = {
    amber: { wrap: isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-600', val: isDarkMode ? 'text-amber-200' : 'text-amber-700', tint: isDarkMode ? 'from-amber-500/10' : 'from-amber-50' },
    emerald: { wrap: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-600', val: isDarkMode ? 'text-emerald-200' : 'text-emerald-700', tint: isDarkMode ? 'from-emerald-500/10' : 'from-emerald-50' },
    rose: { wrap: isDarkMode ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-600', val: isDarkMode ? 'text-rose-300' : 'text-rose-700', tint: isDarkMode ? 'from-rose-500/10' : 'from-rose-50' },
    blue: { wrap: isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600', val: isDarkMode ? 'text-blue-200' : 'text-blue-700', tint: isDarkMode ? 'from-blue-500/10' : 'from-blue-50' },
  };
  const KpiTile = ({ accent, Icon, label, value, unit, active, onClick }) => {
    const a = KPI_ACCENTS[accent];
    return (
      <div onClick={onClick}
        className={`relative overflow-hidden rounded-2xl p-4 cursor-pointer transition-all bg-gradient-to-br ${a.tint} to-transparent ${isDarkMode ? 'bg-white/[0.04] border border-white/12 hover:bg-white/[0.07]' : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'} ${active ? (isDarkMode ? 'ring-2 ring-brand/50' : 'ring-2 ring-brand/40') : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className={labelCls}>{label}</div>
            <div className={`text-2xl font-bold mt-0.5 ${a.val}`}>{value}</div>
          </div>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${a.wrap}`}><Icon className="w-5 h-5" /></div>
        </div>
        <div className={`text-xs ${mutedCls}`}>{unit}</div>
      </div>
    );
  };

  const statusOpts = [
    { value: 'pending', label: t('lm.banfFilterPending') },
    { value: 'approved', label: t('lm.banfFilterApproved') },
    { value: 'rejected', label: t('lm.banfFilterRejected') },
  ];
  const noOtherFilters = !werkF.length && !wgF.length && !anfF.length && !ksF.length && !auftragF.length && !skF.length && !egF.length && !bedarfVon && !bedarfBis && !search;
  const isStatusOnly = (s) => statusF.length === 1 && statusF[0] === s && !riskOnly && noOtherFilters;
  const isRiskOnly = riskOnly && !statusF.length && noOtherFilters;

  const currentFilter = { search, statusF, werkF, wgF, anfF, ksF, auftragF, skF, egF, bedarfVon, bedarfBis, riskOnly, sortBy, sortDir };
  const applyFilter = (s) => {
    if (!s || typeof s !== 'object') return;
    setSearch(s.search || '');
    setStatusF(Array.isArray(s.statusF) ? s.statusF : []);
    setWerkF(Array.isArray(s.werkF) ? s.werkF : []);
    setWgF(Array.isArray(s.wgF) ? s.wgF : []);
    setAnfF(Array.isArray(s.anfF) ? s.anfF : []);
    setKsF(Array.isArray(s.ksF) ? s.ksF : []);
    setAuftragF(Array.isArray(s.auftragF) ? s.auftragF : []);
    setSkF(Array.isArray(s.skF) ? s.skF : []);
    setEgF(Array.isArray(s.egF) ? s.egF : []);
    setBedarfVon(s.bedarfVon || '');
    setBedarfBis(s.bedarfBis || '');
    setRiskOnly(!!s.riskOnly);
    setSortBy(s.sortBy || 'bedarf');
    setSortDir(s.sortDir || 'asc');
    const advUsed = (Array.isArray(s.anfF) && s.anfF.length) || (Array.isArray(s.ksF) && s.ksF.length)
      || (Array.isArray(s.auftragF) && s.auftragF.length) || (Array.isArray(s.skF) && s.skF.length)
      || (Array.isArray(s.egF) && s.egF.length) || s.bedarfVon || s.bedarfBis || s.riskOnly;
    if (advUsed) setShowAdvanced(true);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-5">

        {/* KPI-Karten (klickbar → Drill-down) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile accent="amber" Icon={Clock} label={t('lm.banfKpiPending')} value={kpiPending} unit={t('lm.banfKpiPendingUnit')} active={isStatusOnly('pending')} onClick={() => { resetAll(); setStatusF(['pending']); }} />
          <KpiTile accent="emerald" Icon={CheckCircle2} label={t('lm.banfKpiApproved')} value={kpiApproved} unit={t('lm.banfKpiApprovedUnit')} active={isStatusOnly('approved')} onClick={() => { resetAll(); setStatusF(['approved']); }} />
          <KpiTile accent="rose" Icon={AlertTriangle} label={t('lm.banfKpiRisiko')} value={kpiRisk} unit={t('lm.banfKpiRisikoUnit')} active={isRiskOnly} onClick={() => { resetAll(); setRiskOnly(true); }} />
          <KpiTile accent="blue" Icon={Wallet} label={t('lm.banfKpiWert')} value={fmtEur(kpiWert)} unit={t('lm.banfKpiWertUnit')} active={false} onClick={resetAll} />
        </div>

        {/* Titel */}
        <div>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('lm.banfTitle')}</h2>
          <p className={`text-sm ${mutedCls}`}>{t('lm.banfSubtitle', { count: filtered.length })}</p>
        </div>

        {/* Filterleiste */}
        <div className="space-y-3">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${mutedCls}`} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('lm.banfSearch')} className={`${inputCls} w-full pl-9`} />
          </div>
          {/* Hauptfilter */}
          <div className="flex flex-wrap items-center gap-2">
            <MultiSelectDropdown label={t('lm.banfFilterStatus')} options={statusOpts} selected={statusF} onChange={setStatusF} />
            <MultiSelectDropdown label={t('lm.banfFilterWerk')} options={werkOpts} selected={werkF} onChange={setWerkF} />
            <MultiSelectDropdown label={t('lm.banfFilterWg')} options={wgOpts} selected={wgF} onChange={setWgF} searchable />
            <div className="ml-auto flex items-center gap-2">
              <button type="button" onClick={() => setShowAdvanced((v) => !v)} className={`${chipBase} ${(showAdvanced || advCount > 0) ? chipActive : chipInactive}`}>
                <SlidersHorizontal className="w-3.5 h-3.5" />
                <span>{showAdvanced ? t('lm.lessFilters') : t('lm.moreFilters')}</span>
                {!showAdvanced && advCount > 0 && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDarkMode ? 'bg-brand/40 text-white' : 'bg-brand text-white'}`}>{advCount}</span>}
              </button>
              <span className={`text-xs ${mutedCls}`}>{t('lm.banfSort')}:</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={inputCls}>
                <option value="bedarf">{t('lm.banfSortBedarf')}</option>
                <option value="datum">{t('lm.banfSortDatum')}</option>
                <option value="wert">{t('lm.banfSortWert')}</option>
                <option value="kostenstelle">{t('lm.banfSortKostenstelle')}</option>
                <option value="auftrag">{t('lm.banfSortAuftrag')}</option>
              </select>
              <button type="button" onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} className={inputCls} title={sortDir}>{sortDir === 'asc' ? '↑' : '↓'}</button>
            </div>
          </div>
          {/* Erweiterte Filter */}
          {showAdvanced && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <MultiSelectDropdown label={t('lm.banfFilterAnforderer')} options={anfOpts} selected={anfF} onChange={setAnfF} searchable />
              <MultiSelectDropdown label={t('lm.banfFilterKostenstelle')} options={ksOpts} selected={ksF} onChange={setKsF} searchable />
              <MultiSelectDropdown label={t('lm.banfFilterAuftrag')} options={auftragOpts} selected={auftragF} onChange={setAuftragF} searchable />
              <MultiSelectDropdown label={t('lm.banfFilterSachkonto')} options={skOpts} selected={skF} onChange={setSkF} searchable />
              <MultiSelectDropdown label={t('lm.filterEinkaeufergruppe')} options={egOpts} selected={egF} onChange={setEgF} searchable />
              <div className="flex items-center gap-1">
                <span className={`text-xs ${mutedCls}`}>{t('lm.banfBedarf')}:</span>
                <input type="date" value={bedarfVon} onChange={(e) => setBedarfVon(e.target.value)} className={`${inputCls} py-1 text-xs`} title={t('lm.banfBedarfVon')} />
                <span className={`text-xs ${mutedCls}`}>–</span>
                <input type="date" value={bedarfBis} onChange={(e) => setBedarfBis(e.target.value)} className={`${inputCls} py-1 text-xs`} title={t('lm.banfBedarfBis')} />
              </div>
              <button type="button" onClick={() => setRiskOnly((v) => !v)} className={`${chipBase} ${riskOnly ? chipActive : chipInactive}`}>{t('lm.banfRiskOnly')}</button>
              <AutoFilterToggle user={user} prefKey="banf-auto-filter" currentFilter={currentFilter} onRestore={applyFilter} />
              {anyActive ? <button type="button" onClick={resetAll} className={`text-xs underline ${mutedCls} hover:text-brand`}>{t('lm.banfResetAll')}</button> : null}
            </div>
          )}
        </div>

        {/* 3-State + Liste */}
        {loading ? (
          <div className={`flex items-center justify-center gap-2 py-16 ${mutedCls}`}><Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">{t('lm.loading')}</span></div>
        ) : error ? (
          <div className={`flex flex-col items-center justify-center gap-2 py-16 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}><AlertTriangle className="w-8 h-8" /> <span className="text-sm">{error}</span></div>
        ) : filtered.length === 0 ? (
          <div className={`flex flex-col items-center justify-center gap-3 py-16 ${mutedCls}`}><ClipboardList className="w-10 h-10" /> <span className="text-sm">{t('lm.banfEmpty')}</span></div>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => {
              const open = expandedId === r.id;
              return (
                <div key={r.id} className={card}>
                  <div className="flex flex-wrap items-center justify-between gap-2 cursor-pointer" role="button" tabIndex={0}
                    onClick={() => setExpandedId(open ? null : r.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedId(open ? null : r.id); } }}>
                    <div className="flex items-center gap-2 min-w-0">
                      {open ? <ChevronUp className={`w-4 h-4 ${mutedCls}`} /> : <ChevronDown className={`w-4 h-4 ${mutedCls}`} />}
                      <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-brand/20 text-blue-200' : 'bg-brand/10 text-brand'}`}>{banfNr(r)}</span>
                      <span className={`text-sm font-semibold truncate ${valCls}`}>{firstDesc(r)}</span>
                      {r._atRisk && <span className={riskBadge}>{t('lm.banfRiskBadge')}</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={mutedCls}>{t('lm.banfBedarf')}: {fmtDate(r._bedarf)}</span>
                      <span className={`font-semibold ${valCls}`}>{fmtEur(r.total_amount, r.currency)}</span>
                    </div>
                  </div>

                  {/* Status-Stepper */}
                  <div className="flex items-center mt-3">
                    {STEPS.map((stepKey, idx) => {
                      const st = stepState(r.status, idx);
                      const circle = st === 'done' ? 'bg-brand text-white border-brand'
                        : st === 'current' ? (isDarkMode ? 'border-brand text-brand bg-transparent' : 'border-brand text-brand bg-white')
                        : st === 'rejected' ? 'bg-red-500 text-white border-red-500'
                        : (isDarkMode ? 'border-white/20 text-white/30 bg-transparent' : 'border-slate-300 text-slate-300 bg-white');
                      const line = st === 'done' ? (isDarkMode ? 'bg-brand/60' : 'bg-brand/50') : (isDarkMode ? 'bg-white/10' : 'bg-slate-200');
                      return (
                        <React.Fragment key={stepKey}>
                          <div className="flex flex-col items-center">
                            <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-bold ${circle}`}>
                              {st === 'done' ? <Check className="w-3.5 h-3.5" /> : st === 'rejected' ? <X className="w-3.5 h-3.5" /> : idx + 1}
                            </div>
                            <span className={`mt-1 text-[10px] ${mutedCls}`}>{t(`lm.${stepKey}`)}</span>
                          </div>
                          {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 mb-4 ${line}`} />}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Detail */}
                  {open && (
                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'} space-y-3`}>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                        <span><span className={mutedCls}>{t('lm.banfDetailAnforderer')}: </span><span className={valCls}>{r.requester_name || '—'}</span></span>
                        <span><span className={mutedCls}>{t('lm.banfDetailKostenstelle')}: </span><span className={valCls}>{r.requester_cost_center || '—'}</span></span>
                        <span><span className={mutedCls}>{t('lm.banfErstelltAm')}: </span><span className={valCls}>{fmtDate(r.created_at)}</span></span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className={`border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'} ${mutedCls}`}>
                              <th className="text-left pb-1.5 pr-2 font-semibold">{t('lm.banfDetailPos')}</th>
                              <th className="text-left pb-1.5 pr-2 font-semibold">{t('lm.colMaktx')}</th>
                              <th className="text-left pb-1.5 pr-2 font-semibold">{t('lm.colMatnr')}</th>
                              <th className="text-right pb-1.5 pr-2 font-semibold">{t('lm.banfDetailMenge')}</th>
                              <th className="text-left pb-1.5 pr-2 font-semibold">{t('lm.banfBedarf')}</th>
                              <th className="text-left pb-1.5 pr-2 font-semibold">{t('lm.banfDetailWerk')}</th>
                              <th className="text-left pb-1.5 pr-2 font-semibold">{t('lm.banfKontierung')}</th>
                              <th className="text-left pb-1.5 font-semibold">{t('lm.banfDetailWunschlief')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r._items.map((it, i) => {
                              const hasKont = it.auftrag || it.kostenstelle || it.cost_center || it.sachkonto || it.ps_psp_pnr;
                              return (
                                <tr key={`${r.id}-${i}`} className={`border-b ${isDarkMode ? 'border-white/5' : 'border-slate-50'} ${valCls} align-top`}>
                                  <td className="py-1.5 pr-2 font-mono">{i + 1}</td>
                                  <td className="py-1.5 pr-2">{it.description || '—'}</td>
                                  <td className="py-1.5 pr-2 font-mono">{it.material || '—'}</td>
                                  <td className="py-1.5 pr-2 text-right whitespace-nowrap">{it.quantity} {it.unit || ''}</td>
                                  <td className="py-1.5 pr-2 whitespace-nowrap">{fmtDate(it.delivery_date)}</td>
                                  <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{it.plant || '—'}{(it.lagerort || it.storage_location) ? ` / ${it.lagerort || it.storage_location}` : ''}</td>
                                  <td className="py-1.5 pr-2">
                                    {!hasKont ? '—' : (
                                      <div className="space-y-0.5">
                                        {it.auftrag && <div><span className={mutedCls}>{t('lm.banfKontAuftrag')}: </span>{it.auftrag}</div>}
                                        {(it.kostenstelle || it.cost_center) && <div><span className={mutedCls}>{t('lm.banfKontKostenstelle')}: </span>{it.kostenstelle || it.cost_center}</div>}
                                        {it.sachkonto && <div><span className={mutedCls}>{t('lm.banfKontSachkonto')}: </span>{it.sachkonto}</div>}
                                        {it.ps_psp_pnr && <div><span className={mutedCls}>{t('lm.banfKontPsp')}: </span>{it.ps_psp_pnr}</div>}
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-1.5">{it.preferred_vendor || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {r.approver_comment && (
                        <p className={`text-xs italic ${r.status === 'rejected' ? (isDarkMode ? 'text-red-300' : 'text-red-600') : mutedCls}`}>
                          <FileText className="inline w-3 h-3 mr-1" />{r.approver_comment}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BanfStatusView;
